import { t, useLocale } from "@/i18n/locale";
import { Message } from "@/i18n/Message";
import type { AppSourcePresentation } from "../types";
import { detailsNavigationTarget } from "../discovery/discoveryUrl";
import type { DiscoverySelection } from "../discovery/types";

/** Only source destinations, never the executable app URL. */
export function externalSourceUrl(
  source?: AppSourcePresentation,
): string | null {
  if (!source || source.kind !== "github") return null;
  const href = source.revision_url ?? source.repository_url;
  if (!href) return null;
  try {
    const url = new URL(href);
    return url.protocol === "https:" && !url.username && !url.password
      ? url.href
      : null;
  } catch {
    return null;
  }
}

export default function SourceLink({
  source,
  target,
  onNavigate,
  children = <Message message={"Source"} />,
  className,
}: {
  source?: AppSourcePresentation;
  target: DiscoverySelection;
  onNavigate?: (route: string) => void;
  children?: React.ReactNode;
  className?: string;
}) {
  useLocale();
  // Public masked sources have no link, URL or provenance to disclose.
  if (source?.visibility === "private" && !source.kind)
    return <span title={t("Source private")}><Message message={"Source private"} /></span>;
  const external = externalSourceUrl(source);
  const href =
    external ??
    detailsNavigationTarget(
      target,
      "source",
      typeof window === "undefined"
        ? undefined
        : window.location.pathname + window.location.search,
    );
  return (
    <a
      className={className ?? "app-source-link"}
      href={href}
      {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      onClick={(event) => {
        event.stopPropagation();
        if (
          !external &&
          onNavigate &&
          event.button === 0 &&
          !event.metaKey &&
          !event.ctrlKey &&
          !event.altKey &&
          !event.shiftKey
        ) {
          event.preventDefault();
          onNavigate(href);
        }
      }}
    >
      {children}
    </a>
  );
}
